import { Patient, CONVERT_TO_MGDL } from './Patient';
import { Dose } from './Dose';
import { ConcTime } from './ConcTime';
import moment from 'moment';
import { DoseTypes, PkType } from './utils/constants';
import { Target } from './Target';
import { Pathogen } from './Pathogen';
import { PkParam, PkParamModel } from './PkParam';
import { DrugModel } from './DrugModel';
import { Delivery } from './Delivery';
import { Drug } from './Drug';
import { AmwgSampler } from '../plugins/mcmc.js';
import * as ss from 'simple-statistics';
import { Pk } from './Pk';
import { CalculationType, DeliveryMethod } from './Enums';
import { DynamicDoseRegime } from './DynamicDoseRegime';
import { DynamicDosePlan } from './DynamicDosePlan';
import { SummaryResult } from './SummaryResult';
import { drug_models } from './modelData/DrugModels';
import { Custom } from './Custom';
import { ConcentrationCalculator } from './calculators/ConcentrationCalculator';
import { OneCompartmentConcentrationCalculator } from './calculators/OneCompartmentCalculator';
import {
  TwoCompartmentConcentrationCalculator,
  calculateACompartment,
  calculateBCompartment,
} from './calculators/TwoCompartmentCalculator';
import { calculateBeta, calculateAlpha } from './utils/calculations_2compartment';

const DEBUG = import.meta.env.VITE_DEBUG ? parseInt(import.meta.env.VITE_DEBUG) : 0;

function addDate(start: Date, amount: number, unit: string) {
  if (unit === 'days') {
    return moment(start).add(amount, 'days').toDate();
  } else if (unit === 'minutes') {
    return moment(start).add(amount, 'minutes').toDate();
  }
  return moment(start).add(amount, 'hours').toDate();
}

//* from distributions.js */
function norm(x: number, mean: number, sd: number) {
  return -0.5 * Math.log(2 * Math.PI) - Math.log(sd) - Math.pow(x - mean, 2) / (2 * sd * sd);
}

function unif(x: number, min: number, max: number) {
  return x < min || x > max ? -Infinity : Math.log(1 / (max - min));
}

function convertToHours(amount: number, unit: string): number {
  const timeAdjustment = unit === 'days' ? 24 : unit === 'minutes' ? 1 / 60 : 1;
  return amount * timeAdjustment;
}

class AdministeredDose {
  dose: Dose;
  infusionEndDt: Date;
  infusionHrs: number;
  startConcentration: number;
  infusionEndConcentration: number;
  startHr: number;

  constructor(
    dose: Dose,
    startConcentration: number,
    infusionEndConcentration: number,
    infusionEndDt: Date,
    infusionHrs: number,
    startHr: number,
  ) {
    this.dose = dose;
    this.infusionEndDt = infusionEndDt;
    this.infusionHrs = infusionHrs;
    this.startConcentration = startConcentration;
    this.infusionEndConcentration = infusionEndConcentration;
    this.startHr = startHr;
  }
  getDosePerHour() {
    return this.dose.amount / this.infusionHrs;
  }
}

/**
 * This is a rewrite of the Simulation class which is designed to update as each change it made
 * rather than being triggered by calls to generate.
 */
export class DynamicSimulation {
  // main inputs
  private readonly _patient: Patient | null = null;
  private readonly _model: DrugModel | null = null;
  private readonly _pathology: Pathogen | null = null;
  private readonly _doses: Dose[] = [];
  private readonly _target: Target | null = null;
  private readonly _drug: Drug | null = null;
  private readonly _delivery: Delivery | null = null;
  private _selectedPlan: DynamicDosePlan | null = null;

  // derived data
  private _dosesByType: Record<string, Dose[]> = {};
  private _sawchukZaskePair: null | {
    dose: Dose;
    doseIdx: number;
    firstMeasure: Dose;
    lastMeasure: Dose;
    interval: number;
  } = null;
  private _pkParamModel: PkParamModel = new PkParamModel();
  private _administeredDoses: Record<string, AdministeredDose[]> = {};
  private _administeredDoseConc: Record<string, ConcTime[]> = {};
  private _plansByDeliveryMethod: Record<string, DynamicDosePlan[]> = {};

  constructor(param: {
    patient: Patient | null;
    model?: DrugModel | null;
    modelId?: string | null;
    pathology: Pathogen | null;
    doses: Dose[];
    target:
      | Target
      | null
      | { target: string; pdtMax: number | null; pdtMin: number | null; pdtUnit: string };
    drug?: Drug | null;
    drugId?: string | null;
    delivery: Delivery | null;
  }) {
    if (param.patient) {
      this._patient = Patient.from(param.patient);
    }
    this._patient = param.patient;
    if (param.drug) {
      this._drug = param.drug;
    } else if (param.drugId) {
      for (const d of drug_models) {
        if (d.id === param.drugId) {
          this._drug = d;
        }
      }
    }

    let haveModel = false;
    if (param.model) {
      haveModel = true;
      this._model = param.model;
    } else if (param.modelId) {
      if (this._drug) {
        for (const m of this._drug.models) {
          if (m.code === param.modelId) {
            this._model = m;
            haveModel = true;
          }
        }
      }
    }
    if (!haveModel) {
      for (const d of drug_models) {
        for (const m of d.models) {
          if (m.code === param.modelId) {
            this._model = m;
            this._drug = d;
          }
        }
      }
    }

    this._pathology = param.pathology;
    // copy doses in time order
    this._doses = param.doses
      .map((d) => Dose.from(d))
      .sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
    this._target =
      param.target == null || param.target instanceof Target
        ? Target.from(param.target)
        : new Target({
            type: param.target.target,
            min: param.target.pdtMin,
            max: param.target.pdtMax,
            unit: param.target.pdtUnit,
          });
    this._delivery = Delivery.from(param.delivery);

    this.generateDosesByType();
    if (this.canGeneratePlans()) {
      this.generateSawchukZaskePair();
      this.generatePkParamModel();
      this.generateAdministeredDoseConcentrations();
      this.generateDosePlans();
    }
  }

  public get patient(): Patient | null {
    return this._patient;
  }

  public get model(): DrugModel | null {
    return this._model;
  }

  public get pathology(): Pathogen | null {
    return this._pathology;
  }

  public get doses(): Dose[] {
    return this._doses;
  }

  public get target(): Target | null {
    return this._target;
  }

  public get drug(): Drug | null {
    return this._drug;
  }

  public get delivery(): Delivery | null {
    return this._delivery;
  }

  public get dosesByType(): Record<string, Dose[]> {
    return this._dosesByType;
  }

  public get sawchukZaskePair(): {
    dose: Dose;
    doseIdx: number;
    firstMeasure: Dose;
    lastMeasure: Dose;
  } | null {
    return this._sawchukZaskePair;
  }

  public get pkParamModel(): PkParamModel {
    return this._pkParamModel;
  }

  public get administeredDoses(): Record<string, AdministeredDose[]> {
    return this._administeredDoses;
  }

  public get administeredDoseConc(): Record<string, ConcTime[]> {
    return this._administeredDoseConc;
  }

  public get plansByDeliveryMethod(): Record<string, DynamicDosePlan[]> {
    return this._plansByDeliveryMethod;
  }

  public plansForDeliveryMethod(deliveryMethod: string): DynamicDosePlan[] {
    if (deliveryMethod in this._plansByDeliveryMethod) {
      return this._plansByDeliveryMethod[deliveryMethod] ?? [];
    }
    return [];
  }

  public adminDoseConcForType(calcType: string): ConcTime[] {
    if (calcType in this._administeredDoseConc) {
      return this._administeredDoseConc[calcType] ?? [];
    }
    return [];
  }

  /**
   * Segment the patient administered concentration-time curve into 24-hour windows
   * from the first administered dose and compute AUC per window using the trapezoidal method.
   * Returns an empty array when fewer than one complete 24-hour window is available.
   */
  public get dailyAucs(): { day: number; startTime: Date; endTime: Date; auc: number }[] {
    const concData = this._administeredDoseConc[CalculationType.PATIENT];
    if (!concData || concData.length === 0) return [];

    const firstMs = concData[0]!.time.getTime();
    const lastMs = concData[concData.length - 1]!.time.getTime();
    const totalHrs = (lastMs - firstMs) / (1000 * 60 * 60);

    if (totalHrs < 24) return []; // require at least one complete 24-hour window

    const msPerDay = 24 * 60 * 60 * 1000;
    const numDays = Math.floor(totalHrs / 24);
    const result: { day: number; startTime: Date; endTime: Date; auc: number }[] = [];

    for (let d = 0; d < numDays; d++) {
      const windowStart = firstMs + d * msPerDay;
      const windowEnd = windowStart + msPerDay;
      result.push({
        day: d + 1,
        startTime: new Date(windowStart),
        endTime: new Date(windowEnd),
        auc: this.trapezoidalAuc(concData, windowStart, windowEnd),
      });
    }
    return result;
  }

  private trapezoidalAuc(concData: ConcTime[], startMs: number, endMs: number): number {
    const pts: { t: number; c: number }[] = [];
    pts.push({ t: startMs, c: this.interpolateConc(concData, startMs) });
    for (const pt of concData) {
      const t = pt.time.getTime();
      if (t > startMs && t < endMs) pts.push({ t, c: pt.concentration });
    }
    pts.push({ t: endMs, c: this.interpolateConc(concData, endMs) });

    let auc = 0;
    for (let i = 1; i < pts.length; i++) {
      const dt = (pts[i]!.t - pts[i - 1]!.t) / (1000 * 60 * 60); // hours
      auc += ((pts[i]!.c + pts[i - 1]!.c) / 2) * dt;
    }
    return auc;
  }

  private interpolateConc(concData: ConcTime[], targetMs: number): number {
    let before: ConcTime | null = null;
    let after: ConcTime | null = null;
    for (const pt of concData) {
      const t = pt.time.getTime();
      if (t <= targetMs) before = pt;
      else if (!after) {
        after = pt;
        break;
      }
    }
    if (!before) return after?.concentration ?? 0;
    if (!after) return before.concentration;
    const frac =
      (targetMs - before.time.getTime()) / (after.time.getTime() - before.time.getTime());
    return before.concentration + frac * (after.concentration - before.concentration);
  }

  public get selectedPlan(): DynamicDosePlan | null {
    return this._selectedPlan;
  }

  public set selectedPlan(plan: DynamicDosePlan | null) {
    if (plan) {
      if (this._target) {
        plan.calculateHighlights(this._target);
      }
      plan.calculateTimeSeries(this);
    }
    this._selectedPlan = plan;
  }

  public isReady(): boolean {
    return this.canGeneratePlans();
  }

  private generateDosesByType() {
    for (const dose of this._doses) {
      const type = dose.type;
      if (type in this._dosesByType) {
        this._dosesByType[type]!.push(dose);
      } else {
        this._dosesByType[type] = [dose];
      }
    }
  }
  private hasCreatinineEntry(): boolean {
    return DoseTypes.SECR in this._dosesByType;
  }

  private hasCreatinineClearanceEntry(): boolean {
    return DoseTypes.CLCR in this._dosesByType;
  }

  private canGeneratePlans(): boolean {
    const havePopulatedPatient = this._patient != null && this._patient.isPopulated();
    const havePopulatedTarget = this._target != null && this._target.isPopulated();
    const havePopulatedDelivery = this._delivery != null && this._delivery.isPopulated();
    const haveModel = this._model != null;
    const haveCreatine = this.hasCreatinineEntry() || this.hasCreatinineClearanceEntry();
    // console.log(`canGeneratePlans havePopulatedPatient=${havePopulatedPatient} havePopulatedTarget=${havePopulatedTarget} havePopulatedDelivery=${havePopulatedDelivery} haveModel=${haveModel} haveCreatine=${haveCreatine}`);
    const result =
      havePopulatedPatient &&
      havePopulatedTarget &&
      havePopulatedDelivery &&
      haveModel &&
      haveCreatine;
    return result;
  }

  public hasAdministeredDoses(): boolean {
    return DoseTypes.DOSE in this._dosesByType;
  }

  public hasMeasured(): boolean {
    return DoseTypes.CONC in this._dosesByType;
  }

  private getDoseIndexForHr(dt: number) {
    const dosesSorted = this._dosesByType[DoseTypes.DOSE];
    if (dosesSorted) {
      const startTs = dosesSorted[0]!.datetime.getTime();
      let doseNum = 0;
      for (const doseToTest of dosesSorted) {
        const startHr = (doseToTest.datetime.getTime() - startTs) / (1000 * 60 * 60);
        if (startHr > dt) {
          break; // gone past
        }
        doseNum++;
      }
      if (doseNum >= dosesSorted.length) {
        // went off end
        doseNum = dosesSorted.length - 1;
      }
      return doseNum - 1;
    }
    return 0;
  }

  private getDoseIndexForHrFromAdministered(administeredDoses: AdministeredDose[], dt: number) {
    let doseNum = 0;
    for (const adminDose of administeredDoses) {
      if (adminDose.startHr > dt) {
        break; // gone past
      }
      doseNum++;
    }
    if (doseNum >= administeredDoses.length) {
      // went off end
      doseNum = administeredDoses.length - 1;
    } else {
      doseNum--;
    }
    return doseNum;
  }

  private generateSawchukZaskePair() {
    console.log('generateSawchukZaskePair');
    if (!(this.hasAdministeredDoses() && this.hasMeasured())) {
      // no data
      return;
    }

    const doses: Dose[] = this._dosesByType[DoseTypes.DOSE] ?? [];
    const measured: Dose[] = this._dosesByType[DoseTypes.CONC] ?? [];

    const startdt = doses[0]!.datetime.getTime();
    let currentFoundDose = -1;
    let firstMeasureInDose = null;
    let lastMeasureInDose = null;
    const candidates = [];
    for (const measurement of measured) {
      const hrsSinceStart = (measurement.datetime.getTime() - startdt) / (1000.0 * 60 * 60);
      const doseOfMeasurement = this.getDoseIndexForHr(hrsSinceStart);
      if (doseOfMeasurement == 1 || doseOfMeasurement == 2) {
        // not considered valid
        continue;
      }
      if (doseOfMeasurement != currentFoundDose) {
        if (firstMeasureInDose != null && lastMeasureInDose != null) {
          // we found a candidate
          candidates.push({
            dose: doses[currentFoundDose]!,
            doseIdx: currentFoundDose,
            firstMeasure: firstMeasureInDose,
            lastMeasure: lastMeasureInDose,
            // approximate interval by taking time to this dose and dividing by how many doses so far.
            interval:
              currentFoundDose > 0
                ? (doses[currentFoundDose]!.datetime.getTime() - startdt) /
                  (1000.0 * 60 * 60) /
                  currentFoundDose
                : 0,
          });
        }
        currentFoundDose = doseOfMeasurement;
        firstMeasureInDose = measurement;
        lastMeasureInDose = null;
      } else {
        lastMeasureInDose = measurement;
      }
    }
    if (firstMeasureInDose != null && lastMeasureInDose != null) {
      // we found a candidate
      candidates.push({
        dose: doses[currentFoundDose]!,
        doseIdx: currentFoundDose,
        firstMeasure: firstMeasureInDose,
        lastMeasure: lastMeasureInDose,
        // approximate interval by taking time to this dose and dividing by how many doses so far.
        interval:
          currentFoundDose > 0
            ? (doses[currentFoundDose]!.datetime.getTime() - startdt) /
              (1000.0 * 60 * 60) /
              currentFoundDose
            : 0,
      });
    }

    if (candidates.length > 0) {
      this._sawchukZaskePair = candidates[candidates.length - 1]!;
    }
  }

  // Calculate creatinine clearance from serum creatinine by CockcroftGault
  // @params sim Simulation
  // return number (mL/min)
  // Ref: https://www.mdcalc.com/calc/43/creatinine-clearance-cockcroft-gault-equation#evidence
  // mL/min = (140 – age) × (weight, kg) × (0.85 if female) / (72 × Cr, mg/dL)
  // Convert: 1 mg/dL = 0.01131222 µmol/L
  private calculateCockcroftGault(): number | null {
    console.log('Using CockcroftGault method');
    let crcl = null;

    if (this._patient && this._patient.age && this.hasCreatinineEntry() && this._model) {
      const genderFactor = this._patient.gender === 'Female' ? 0.85 : 1;
      const ageYears =
        this._patient.ageUnit === 'years' ? this._patient.age : this._patient.age / 365.25;
      const crDose = this._dosesByType[DoseTypes.SECR]?.[0];
      if (crDose && crDose.amount) {
        // scr in mg/dL
        let scr =
          crDose.amountUnit === 'umol/L' ? crDose.amount * CONVERT_TO_MGDL : crDose.amount * 1;
        // Model-specific SCr correction (e.g. Buelga 2005 formula, Goti 2018 age>60 floor)
        scr = this._model.correctSCR(scr, ageYears);
        // Adjust for BMI
        const bmi = this._patient.bmi();
        if (bmi) {
          const weight =
            bmi < 18.5
              ? this._patient.weightKg(false)
              : bmi < 25
                ? this._patient.weightKg(true)
                : this._patient.weightKg(true, true);
          if (weight != null) {
            crcl = ((140 - ageYears) * weight * genderFactor) / (72 * scr);
            if (DEBUG) {
              console.log(
                `CockcroftGault: creatinine: ${crDose.amount} applied weight: ${weight}kg -> crcl=${crcl}`,
              );
            }
          }
        }
        // Cap with model capCrcl
        const cap = this._model.default.capCrcl;
        if (cap && crcl && crcl > cap) {
          crcl = cap;
          if (DEBUG) {
            console.log(`CockcroftGault: CrCl is capped: crcl=${crcl}`);
          }
        }
      }
    }
    return crcl;
  }

  private generatePopulationPK(crcl: number) {
    const pkparams: PkParam[] = [];
    if (this._patient && this._model) {
      const weight = this._patient.weightKg(false, false);
      if (weight) {
        // Patient pkparams
        const bsa = this._patient.bsa() ?? 1;
        pkparams.push(
          new PkParam({
            key: 'ideal',
            label: 'BW Ideal',
            value: this._patient.weightKg(true),
            unit: 'kg',
            type: PkType.PATIENT,
          }),
        );
        pkparams.push(
          new PkParam({
            key: 'adjusted',
            label: 'BW Adjusted',
            value: this._patient.weightKg(true, true),
            unit: 'kg',
            type: PkType.PATIENT,
          }),
        );
        pkparams.push(
          new PkParam({
            key: 'bmi',
            label: 'BMI',
            value: this._patient.bmi(),
            unit: '',
            type: PkType.PATIENT,
          }),
        );
        pkparams.push(
          new PkParam({
            key: 'bsa',
            label: 'BSA',
            value: bsa,
            unit: 'm2',
            type: PkType.PATIENT,
          }),
        );
        pkparams.push(
          new PkParam({
            key: 'crcl',
            label: 'Creatinine CL',
            value: crcl.toFixed(2),
            unit: 'mL/min',
            type: PkType.PATIENT,
          }),
        );

        // One or more compartment models
        if (this._model.getCompartment() === 1) {
          const vdWeight =
            this._model.vd[0]!.unit === 'L/kg IBW'
              ? (this._patient.weightKg(true) ?? weight)
              : weight;
          let Vd: number =
            this._model.vd[0]!.unit === 'L'
              ? this._model.vd[0]!.coeff
              : this._model.vd[0]!.coeff * vdWeight;
          // Clearance of drug based on model coeff and patient crcl
          let drugCl: number = this._model.drugClearance(
            crcl,
            bsa,
            weight,
            this._patient?.dialysis,
          );
          // apply interindividual variability
          if (this._delivery?.applyInterindividualVariability) {
            Vd = Vd * Math.exp(this._model.vd[0]!.sd * this._model.vd[0]!.sd);
            // apply interindividual variability
            drugCl = drugCl * Math.exp(this._model.clearance[0]!.sd * this._model.clearance[0]!.sd);
          }
          // const Vd: number = this._model.vd[0].coeff * weight;
          const Vd_sd: number = Vd * this._model.vd[0]!.sd;
          const drugCl_sd: number = drugCl * this._model.clearance[0]!.sd;
          const kel: number = drugCl / Vd;
          const halfLife: number = (0.693 * Vd) / drugCl;
          pkparams.push(
            new PkParam({
              key: 'drugcl',
              label: 'Drug CL',
              value: drugCl.toFixed(2),
              sd: drugCl_sd.toFixed(2),
              unit: 'L/hr',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'vd',
              label: 'Vd',
              value: Vd.toFixed(2),
              sd: Vd_sd.toFixed(2),
              unit: 'L',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'kel',
              label: 'Kel',
              value: kel.toFixed(4),
              unit: '/hr',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 't12',
              label: 'Half-life',
              value: halfLife.toFixed(2),
              unit: 'hrs',
              type: PkType.EMPIRIC,
            }),
          );
          if (DEBUG) {
            console.log(`generatePk: Population one-cpt drugCl=${drugCl}ml/hr vd=${Vd} kel=${kel}`);
          }
        } else if (this._model.getCompartment() === 2 && this._model.vd.length === 2) {
          const v1: Pk = this._model.vd[0]!;
          let Vc: number = v1.unit === 'L/kg' ? v1.coeff * weight : v1.coeff;
          // GOTI2018: Vc × 0.5 for hemodialysis patients (Vc = 58.4 × (WT/70) × 0.5^DIAL)
          if (this._model.code === 'GOTI2018' && this._patient?.dialysis) Vc *= 0.5;
          const Vc_sd: number = Vc * v1.sd; // absolute SD = volume × CV fraction (same as 1-cpt branch)
          const v2: Pk = this._model.vd[1]!;
          const Vp: number = v2.unit === 'L/kg' ? v2.coeff * weight : v2.coeff;
          const Vp_sd: number = Vp * v2.sd; // absolute SD = volume × CV fraction
          const drugCl2 = this._model.clearance.filter((c) => c.type === 'central')[0]!;
          // Patient-adjusted clearance (applies CrCl formula from the model, same as 1-cpt branch)
          const drugCl: number = this._model.drugClearance(
            crcl,
            bsa,
            weight,
            this._patient?.dialysis,
          );
          const drugCl_sd: number = drugCl * drugCl2.sd;
          const Q: number = this._model.clearance.filter((c) => c.type === 'intercompartmental')[0]!
            .coeff;
          const beta: number = calculateBeta(Q, Vc, Vp, drugCl);
          const alpha: number = calculateAlpha(Q, Vc, Vp, drugCl, beta);
          const A: number = calculateACompartment(Q, Vc, Vp, alpha, beta);
          const B: number = calculateBCompartment(Q, Vc, Vp, alpha, beta);
          pkparams.push(
            new PkParam({
              key: 'drugcl',
              label: 'Drug CL',
              value: drugCl.toFixed(2),
              sd: drugCl_sd.toFixed(2),
              unit: 'L/hr',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'Vc',
              label: 'Vd (central)',
              value: Vc.toFixed(2),
              sd: Vc_sd.toFixed(2),
              unit: 'L',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'Vp',
              label: 'Vd (peripheral)',
              value: Vp.toFixed(2),
              sd: Vp_sd.toFixed(2),
              unit: 'L',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'Q',
              label: 'Q (intercompartmental)',
              value: Q.toFixed(2),
              unit: 'L/h',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'alpha',
              label: 'Alpha (distribution)',
              value: alpha.toFixed(2),
              unit: '',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'beta',
              label: 'Beta (elimination)',
              value: beta.toFixed(2),
              unit: '',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'A',
              label: 'A (distribution)',
              value: A?.toFixed(2),
              unit: '',
              type: PkType.EMPIRIC,
            }),
          );
          pkparams.push(
            new PkParam({
              key: 'B',
              label: 'B (elimination)',
              value: B?.toFixed(2),
              unit: '',
              type: PkType.EMPIRIC,
            }),
          );
          if (DEBUG) {
            console.log(
              `generatePk: Population 2-cpt drugCl=${drugCl}ml/hr Vc=${Vc} Vp=${Vp} Q=${Q}`,
            );
          }
        } else {
          throw Error('Unable to proceed with simulation - missing model info');
        }

        this._pkParamModel.addAllParams(pkparams);
      } else {
        console.error('No patient CrCl to generate pkParams');
        throw Error('Unable to proceed with simulation - missing Crcl');
      }
    } else {
      console.error('No patient weight to generate pkParams');
      throw Error('Unable to proceed with simulation - missing weight');
    }
  }

  private hasSawchukInterval() {
    return this._sawchukZaskePair != null;
  }

  // Calculate Kel from two concentrations over time
  // @params conc1: first concentration (mcg/ml)
  // @params conc2: second concentration (mcg/ml)
  // @params dt: time difference (hr)
  calculateKelFromConcentrations(conc1: number, conc2: number, dt: number): number {
    return Math.log(conc1 / conc2) / dt;
  }

  // Sawchuk-Zaske method for 2 measured concns at initial or steady state (> 3 doses)
  // NB: this does not use any model population values
  calculatePkBySawchukZaske(): null | { kel: string; vd: string; cl: string; half: string } {
    let rtn: null | { kel: string; vd: string; cl: string; half: string } = null;
    if (this._sawchukZaskePair == null) {
      return null;
    }

    console.log(`SawchukZaske method for fitting`);
    const idx = this._sawchukZaskePair.doseIdx;
    // use first interval or after 3 or more doses (ss)
    if (idx === 0 || idx > 2) {
      const idx = this._sawchukZaskePair.doseIdx;
      const dose: Dose = this._sawchukZaskePair.dose;
      const m0: Dose = this._sawchukZaskePair.firstMeasure; // detect first measured in interval
      const m1: Dose = this._sawchukZaskePair.lastMeasure; // detect last measured in interval
      const dt: number = (m1.datetime.getTime() - m0.datetime.getTime()) / (1000.0 * 60 * 60);
      // Determine slope (Kel) from measured concentrations: ln(C0/C1) / dt
      const kel: number = this.calculateKelFromConcentrations(m0.amount, m1.amount, dt);
      const t_half: number = 0.693 / kel;
      // For Vd, Extrapolate to Cmax and Cmin by subtracting the time of first conc from time of dose + infusion
      // Cp=Cp0 * exp(-k.t)
      // Find cmax
      const endInfusion = addDate(dose.datetime, dose.duration, dose.durationUnit);
      const t1: number =
        (new Date(m0.datetime).getTime() - endInfusion.getTime()) / (1000.0 * 60 * 60); //ms to hr
      const cmax: number = m0.amount / Math.exp(-kel * t1);
      // calculated trough from measured trough at time before start of next infusion
      // If this is the first dose, set cmin to 0 (ie, not at ss)
      let cmin = 0;
      if (idx !== 0) {
        // calculate a cmin
        const endInterval = addDate(dose.datetime, this._sawchukZaskePair.interval, 'hours');
        const timeToDecay = (endInterval.getTime() - m1.datetime.getTime()) / (1000.0 * 60 * 60);
        cmin = m1.amount * Math.exp(-kel * timeToDecay);
      }
      const vd: number =
        ((dose.amount / dose.duration) * (1 - Math.exp(-kel * dose.duration))) /
        (kel * (cmax - cmin * Math.exp(-kel * dose.duration)));
      // recalculate with new vd, kel
      const cl: number = kel * vd;
      if (DEBUG) {
        console.log(
          `SawchukZaske - patient m0: ${m0.amount} t0: ${t1} m1: ${m1.amount} t1: ${t1} dt: ${dt} cl: ${cl}ml/hr vd: ${vd} cmax: ${cmax} cmin: ${cmin}`,
        );
      }
      rtn = {
        kel: kel.toFixed(4),
        vd: vd.toFixed(2),
        cl: cl.toFixed(2),
        half: t_half.toFixed(2),
      };
    }
    return rtn;
  }

  // Get Data as object with x,y arrays where x = time since end of infusion (hrs), y is measured conc (mg/ml)
  // @param doses : array of Dose with at least one dose and drug level - SORTED in ascending by datetime (removed from here bs very slow)
  // @return: params object with estimates of vd (vd_est), cl (cl_est), kel (kel_est), halflife
  // OR null if not enough doses provided
  private getXYData(): { x: number[]; y: number[] } | null {
    const drugLevels = this._dosesByType[DoseTypes.CONC] ?? [];
    const dosesSorted = this._dosesByType[DoseTypes.DOSE] ?? [];
    if (dosesSorted.length >= 1 && drugLevels.length >= 1) {
      const startTime = new Date(dosesSorted[0]!.datetime);
      // const duration_hr = dosesSorted[0].getInfusionDurationInHours();
      const x_arr = [];
      const y_arr = [];

      for (let i = 0; i < drugLevels.length; i++) {
        const dt = (drugLevels[i]!.datetime.getTime() - startTime.getTime()) / (1000.0 * 60 * 60); // - duration_hr
        if (!isNaN(dt) && dt > 0) {
          x_arr.push(dt);
          y_arr.push(drugLevels[i]!.amount);
        } else {
          console.error('Error in getXYData values');
          return null;
        }
      }
      return {
        x: x_arr,
        y: y_arr,
      };
    } else {
      return null;
    }
  }

  // Calculate the concentration at a given time (dt)
  // @param vd: vol dist
  // @param cl: drug clearance
  // @param dt: time at which concentration required (hours from time of c0)
  private calculateConcentrationAtTime(
    administeredDoses: AdministeredDose[],
    calculator: ConcentrationCalculator,
    dt: number,
  ) {
    const doseToUse =
      administeredDoses[this.getDoseIndexForHrFromAdministered(administeredDoses, dt)]!;

    const infusionEndHr = doseToUse.startHr + doseToUse.infusionHrs;
    if (dt == infusionEndHr) {
      return doseToUse.infusionEndConcentration;
    } else if (dt < infusionEndHr) {
      // inside infusion period
      const hrsToInfuse = dt - doseToUse.startHr;
      return calculator.concentrationDuringInfusion(
        doseToUse.startConcentration,
        doseToUse.getDosePerHour(),
        hrsToInfuse,
        dt,
      );
    } else {
      // in decay perion
      const hrsToDecay = dt - infusionEndHr;
      return calculator.concentrationDuringDecay(
        doseToUse.infusionEndConcentration,
        hrsToDecay,
        dt,
      );
    }
  }

  // run Bayesian MCMC stepper to calculate pkParams from measured concentrations
  // @params pop_params : object with cl, vd
  // @params doses : all doses and measurements for patient
  // @returns params with pop_params and estimated params
  runBayesianFit(pop_params: { cl: number; vd: number; cl_sd: number; vd_sd: number }): {
    cl: number;
    vd: number;
    cl_sd: number;
    vd_sd: number;
    vd_est?: number;
    cl_est?: number;
    kel_est?: number;
    halflife_est?: number;
  } {
    const params = {
      cl: { type: 'real', init: pop_params.cl },
      vd: { type: 'real', init: pop_params.vd },
      sigma: { type: 'real', lower: 0 },
    };

    const data = this.getXYData();
    console.log(data);
    if (data) {
      const sample_iterations = 10;
      const samples_per_iteration = 10000;
      const samples_to_burn = 100;
      console.log(`Running Bayesian fit with ${samples_per_iteration} samples per iteration`);
      let samples;

      const arr_cl = [];
      const arr_vd = [];

      const options = {
        thin: 10,
        target_accept_rate: 0.6,
      };

      const log_post = (
        state: { cl: number; vd: number; sigma: number },
        data: { x: number[]; y: number[] },
      ) => {
        let log_post = 0;
        const k = state.cl / state.vd;
        const calculator = new OneCompartmentConcentrationCalculator(k, state.vd);
        const administeredDoses = this.calculateAdministered(calculator);

        // Priors
        log_post += norm(state.cl, pop_params.cl, pop_params.cl_sd);
        log_post += norm(state.vd, pop_params.vd, pop_params.vd_sd);
        log_post += unif(state.sigma, 0, 1);

        // Likelihood
        for (let i = 0; i < data.y.length; i++) {
          const mu = this.calculateConcentrationAtTime(administeredDoses, calculator, data.x[i]!);
          // console.log(state.cl, state.vd, mu);
          // if (DEBUG) {
          //     console.log(`mu=${mu}`)
          // }
          const n = norm(data.y[i]!, mu, state.sigma);
          log_post += n;
        }
        if (isNaN(log_post)) {
          console.error('Error - log_post is NaN', data);
          log_post = 0;
        }

        return log_post;
      };

      for (let samp_i = 0; samp_i < sample_iterations; samp_i++) {
        const sampler = new AmwgSampler(params, log_post, data, options);
        sampler.burn(samples_to_burn);
        samples = sampler.sample(samples_per_iteration);
        // Capture mean of output
        arr_cl.push(ss.mean(samples['cl']!));
        arr_vd.push(ss.mean(samples['vd']!));
      }
      const vd_est = parseFloat(ss.mean(arr_vd).toFixed(2));
      const cl_est = parseFloat(ss.mean(arr_cl).toFixed(2));
      const kel_est = cl_est / vd_est;
      const halflife_est = 0.693 / kel_est;

      return {
        ...pop_params,
        vd_est,
        cl_est,
        kel_est: parseFloat(kel_est.toFixed(4)),
        halflife_est: parseFloat(halflife_est.toFixed(4)),
      };
    } else {
      return pop_params;
    }
  }

  calculatePkByBayesian(
    pop_clearance: PkParam,
    pop_vd: PkParam,
  ): null | {
    kel: string | undefined;
    vd: string | undefined;
    cl: string | undefined;
    half: string | undefined;
  } {
    if (pop_clearance && pop_vd) {
      const pop_params = {
        cl:
          typeof pop_clearance.value === 'string'
            ? parseFloat(pop_clearance.value)
            : (pop_clearance.value ?? 0),
        cl_sd:
          typeof pop_clearance.sd === 'string'
            ? parseFloat(pop_clearance.sd)
            : (pop_clearance.sd ?? 0),
        vd: typeof pop_vd.value === 'string' ? parseFloat(pop_vd.value) : (pop_vd.value ?? 0),
        vd_sd: typeof pop_vd.sd === 'string' ? parseFloat(pop_vd.sd) : (pop_vd.sd ?? 0),
      };
      const pkparams = this.runBayesianFit(pop_params);
      if (DEBUG) {
        console.log(pop_params, pkparams);
      }
      return {
        kel: pkparams.kel_est?.toFixed(4),
        vd: pkparams.vd_est?.toFixed(4),
        cl: pkparams.cl_est?.toFixed(4),
        half: pkparams.halflife_est?.toFixed(4),
      };
    }
    return null;
  }

  // For one compartment models
  // Step 1a. If patient has measured concentrations,
  // re-estimate cl, kel and vd
  // using Bayesian MCMC stepper
  // works best with 1 or more measurements in a single dose interval
  generatePkFromMeasured() {
    const doses: Dose[] | null = this._dosesByType[DoseTypes.DOSE] ?? null;
    const measured: Dose[] | null = this._dosesByType[DoseTypes.CONC] ?? null;
    const pop_clearance = this._pkParamModel.getPk('drugcl');
    const pop_vd = this._pkParamModel.getPk('vd');
    if (
      pop_clearance &&
      pop_vd &&
      doses != null &&
      measured != null &&
      doses.length > 0 &&
      measured.length > 0
    ) {
      // to use Sawchuk Zaske we need 2 measurements in the same dose after infusion
      // for best results closer to steady state is better.
      // Sawchuk if 2 m in same interval for dose=1 or > 3 else Bayesian fit
      const pk: {
        kel: string | undefined;
        vd: string | undefined;
        cl: string | undefined;
        half: string | undefined;
      } | null = this.hasSawchukInterval()
        ? this.calculatePkBySawchukZaske()
        : this.calculatePkByBayesian(pop_clearance, pop_vd);
      if (pk) {
        if (DEBUG) {
          console.log(
            `generatePkFromMeasured - optimised patient cl: ${pk.cl}ml/hr vd: ${pk.vd} kel: ${pk.kel}`,
          );
        }

        this._pkParamModel.addParam(
          new PkParam({
            key: 'drugcl_p',
            label: 'Drug CL',
            value: pk.cl,
            unit: 'L/hr',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 'vd_p',
            label: 'Vd',
            value: pk.vd,
            unit: 'L',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 'kel_p',
            label: 'Kel',
            value: pk.kel,
            unit: '/hr',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 't12_p',
            label: 'Half-life',
            value: pk.half,
            unit: 'hrs',
            type: PkType.OPTIMISED,
          }),
        );
      }
    }
  }

  private runBayesianFit2(pop_params: {
    q: number;
    cl: number;
    vc: number;
    vp: number;
    vc_sd: number;
    vp_sd: number;
  }): {
    q: number;
    cl: number;
    sigma?: number;
    vc: number;
    vp: number;
    vc_sd: number;
    vp_sd: number;
    vc_est?: number;
    vp_est?: number;
    a_est?: number;
    b_est?: number;
    alpha?: number;
    beta?: number;
  } {
    const params = {
      vc: { type: 'real', init: pop_params.vc },
      vp: { type: 'real', init: pop_params.vp },
      sigma: { type: 'real', lower: 0 },
    };

    const q = pop_params.q;
    const cl = pop_params.cl;

    const data = this.getXYData();
    if (data) {
      const sample_iterations = 5;
      const samples_per_iteration = 10000;
      const samples_to_burn = 100;
      console.log(`Running Bayesian fit 2 with ${samples_per_iteration} samples per iteration`);
      let samples;

      const arr_vc = [];
      const arr_vp = [];

      const options = {
        thin: 10,
        target_accept_rate: 0.6,
      };

      const log_post = (
        state: {
          q: number;
          cl: number;
          sigma: number;
          vc: number;
          vp: number;
          vc_sd: number;
          vp_sd: number;
        },
        data: { x: number[]; y: number[] },
      ) => {
        let log_post = 0;

        // Priors
        log_post += norm(state.vc, pop_params.vc, pop_params.vc_sd);
        log_post += norm(state.vp, pop_params.vp, pop_params.vp_sd);
        log_post += unif(state.sigma, 0, 1);

        const beta = calculateBeta(q, state.vc, state.vp, cl);
        const alpha = calculateAlpha(q, state.vc, state.vp, cl, beta);
        const A = calculateACompartment(q, state.vc, state.vp, alpha, beta);
        const B = calculateBCompartment(q, state.vc, state.vp, alpha, beta);

        const calculator = new TwoCompartmentConcentrationCalculator(
          state.vc,
          alpha,
          beta,
          A,
          B,
          () => this.adminDoseGenerator(),
        );
        const administeredDoses = this.calculateAdministered(calculator);

        // Likelihood
        for (let i = 0; i < data.y.length; i++) {
          const mu = this.calculateConcentrationAtTime(administeredDoses, calculator, data.x[i]!);
          if (DEBUG) {
            console.log(`mu=${mu}`);
          }
          const n = norm(data.y[i]!, mu, state.sigma);
          log_post += n;
        }
        if (isNaN(log_post)) {
          console.error('Error - log_post is NaN', data);
          log_post = 0;
        }

        return log_post;
      };

      for (let samp_i = 0; samp_i < sample_iterations; samp_i++) {
        const sampler = new AmwgSampler(params, log_post, data, options);
        sampler.burn(samples_to_burn);
        samples = sampler.sample(samples_per_iteration);
        // Capture mean of output
        arr_vc.push(ss.mean(samples['vc']!));
        arr_vp.push(ss.mean(samples['vp']!));
      }
      const vc_est = parseFloat(ss.mean(arr_vc).toFixed(2));
      const vp_est = parseFloat(ss.mean(arr_vp).toFixed(2));
      const beta = calculateBeta(q, vc_est, vp_est, cl);
      const alpha = calculateAlpha(q, vc_est, vp_est, cl, beta);
      const a_est = calculateACompartment(q, vc_est, vp_est, alpha, beta);
      const b_est = calculateBCompartment(q, vc_est, vp_est, alpha, beta);

      return {
        ...pop_params,
        vc_est,
        vp_est,
        a_est,
        b_est,
        alpha,
        beta,
      };
    } else {
      return pop_params;
    }
  }

  private calculatePkByBayesian2(
    pop_vc: PkParam,
    pop_vp: PkParam,
    pop_cl: number,
    pop_q: number,
  ): { vc: number; vp: number; alpha: number; beta: number; A: number; B: number } | null {
    if (pop_vc && pop_vp) {
      const pop_params = {
        vc: pop_vc.getValueOr(NaN), // should never use default
        vc_sd: pop_vc.getSdOr(NaN),
        vp: pop_vp.getValueOr(NaN), // should never use default
        vp_sd: pop_vp.getSdOr(NaN),
        q: pop_q,
        cl: pop_cl,
      };
      const params = this.runBayesianFit2(pop_params);
      if (DEBUG) {
        console.log(params);
      }
      return {
        vc: params.vc_est ?? NaN,
        vp: params.vp_est ?? NaN,
        A: params.a_est ?? NaN,
        alpha: params.alpha ?? NaN,
        B: params.b_est ?? NaN,
        beta: params.beta ?? NaN,
      };
    }
    return null;
  }

  // For 2 compartment models
  // If patient has measured concentrations - re-estimate A, alpha, B, beta - Bayesian only
  generatePkFromMeasured2() {
    const doses: Dose[] = this.hasAdministeredDoses()
      ? (this._dosesByType[DoseTypes.DOSE] ?? [])
      : [];
    const measured: Dose[] = this.hasMeasured() ? (this._dosesByType[DoseTypes.CONC] ?? []) : [];
    const pop_vc = this._pkParamModel.getPk('Vc');
    const pop_vp = this._pkParamModel.getPk('Vp');
    const pop_q = this._pkParamModel.getPkValue('Q');
    const pop_cl = this._pkParamModel.getPkValue('drugcl');
    if (doses.length > 0 && measured.length > 0 && pop_vc && pop_vp && pop_q && pop_cl) {
      const pk = this.calculatePkByBayesian2(pop_vc, pop_vp, pop_cl, pop_q);
      if (pk) {
        if (DEBUG) {
          console.log(
            `generatePkFromMeasured2compartment optimised vc: ${pk.vc} vp: ${pk.vp} A: ${pk.A} alpha: ${pk.alpha} B: ${pk.B} beta: ${pk.beta}`,
          );
        }

        this._pkParamModel.addParam(
          new PkParam({
            key: 'Vc_p',
            label: 'Vd (central)',
            value: pk.vc,
            unit: 'L',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 'Vp_p',
            label: 'Vd (peripheral)',
            value: pk.vp,
            unit: 'L',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 'A_p',
            label: 'A (distribution)',
            value: pk.A,
            unit: '',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 'alpha_p',
            label: 'Alpha (distribution)',
            value: pk.alpha,
            unit: '',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 'B_p',
            label: 'B (elimination)',
            value: pk.B,
            unit: '',
            type: PkType.OPTIMISED,
          }),
        );
        this._pkParamModel.addParam(
          new PkParam({
            key: 'beta_p',
            label: 'Beta (elimination)',
            value: pk.beta,
            unit: '',
            type: PkType.OPTIMISED,
          }),
        );
      }
    }
  }

  private generatePkParamModel() {
    const crcl: number | null = this.hasCreatinineEntry()
      ? this.calculateCockcroftGault()
      : (this._dosesByType[DoseTypes.CLCR]?.[0]?.amount ?? null);
    if (crcl) {
      // 1. use population model and patient CrCl to calculate Kel, Vd
      this.generatePopulationPK(crcl);
      // 2. if patient has measured doses, re-estimate Kel, Vd from plasma concentration/s
      if (this.hasMeasured() && this._model) {
        if (this._model.getCompartment() === 1) {
          this.generatePkFromMeasured();
        } else if (this._model.getCompartment() === 2) {
          this.generatePkFromMeasured2();
        }
      }
    }
  }

  private getCalculator(
    calculationType: CalculationType,
    doseGenerator:
      | (() => Generator<{ startHr: any; dose: any; infusion: any; interval: number }, void, void>)
      | null = null,
  ): ConcentrationCalculator | null {
    if (this._model) {
      const pkModifier = calculationType == CalculationType.POPULATION ? '' : '_p';
      if (this._model.getCompartment() === 1) {
        const kel = this._pkParamModel.getPkValue('kel' + pkModifier);
        const vd = this._pkParamModel.getPkValue('vd' + pkModifier);
        if (kel != null && vd != null) {
          return new OneCompartmentConcentrationCalculator(kel, vd);
        }
      } else {
        // Read Vc/Vp/Q/CL and recompute alpha/beta/A/B to avoid rounding errors.
        // The stored pkParam strings are 2dp which causes A≈0.025 → 0.03 (20% error)
        // and beta≈0.062 → 0.06, making B/beta 25% too high.
        const vc = this._pkParamModel.getPkValue('Vc' + pkModifier);
        const vp = this._pkParamModel.getPkValue('Vp' + pkModifier);
        // Q and CL are population-level (no patient-specific variant in 2-cpt Bayesian)
        const q = this._pkParamModel.getPkValue('Q');
        const cl = this._pkParamModel.getPkValue('drugcl');
        if (vc != null && vp != null && q != null && cl != null) {
          const beta = calculateBeta(q, vc, vp, cl);
          const alpha = calculateAlpha(q, vc, vp, cl, beta);
          const A = calculateACompartment(q, vc, vp, alpha, beta);
          const B = calculateBCompartment(q, vc, vp, alpha, beta);
          return new TwoCompartmentConcentrationCalculator(vc, alpha, beta, A, B, doseGenerator);
        }
      }
    }
    return null;
  }

  private calculateAdministered(concCalculator: ConcentrationCalculator): AdministeredDose[] {
    const result: AdministeredDose[] = [];

    if (DoseTypes.DOSE in this._dosesByType && this._dosesByType[DoseTypes.DOSE]!.length > 0) {
      let startConcentration = 0.0;
      let lastAdminDose: AdministeredDose | null = null;
      const startTime = this._dosesByType[DoseTypes.DOSE]![0]!.datetime.getTime();
      let timeDelta = 0;
      for (const dose of this._dosesByType[DoseTypes.DOSE]!) {
        if (lastAdminDose) {
          // work out decay to the start of this dose.
          const hrsToDecay =
            (dose.datetime.getTime() - lastAdminDose.infusionEndDt.getTime()) / (1000.0 * 60 * 60);
          timeDelta += hrsToDecay;
          startConcentration = concCalculator.concentrationDuringDecay(
            lastAdminDose.infusionEndConcentration,
            hrsToDecay,
            timeDelta,
          );
        }
        const infusionInHrs = dose.getInfusionDurationInHours();
        const dosePerHr = dose.amount / infusionInHrs;
        timeDelta += infusionInHrs;
        const infusionEndConcentration = concCalculator.concentrationDuringInfusion(
          startConcentration,
          dosePerHr,
          infusionInHrs,
          timeDelta,
        );
        lastAdminDose = new AdministeredDose(
          dose,
          startConcentration,
          infusionEndConcentration,
          addDate(dose.datetime, dose.duration, dose.durationUnit),
          infusionInHrs,
          (dose.datetime.getTime() - startTime) / (1000.0 * 60 * 60),
        );
        result.push(lastAdminDose);
      }
    }

    return result;
  }

  private calculateAdminConcentrations(
    calculationType: CalculationType,
    adminDoses: AdministeredDose[],
    concCalculator: ConcentrationCalculator,
  ): ConcTime[] {
    const result: ConcTime[] = [];

    let doseNum = 0;
    for (const dose of adminDoses) {
      const infusionsStart = dose.startHr;
      const start = dose.dose.datetime;
      const startConc = dose.startConcentration;
      const dosePerHr = dose.getDosePerHour();

      // add concentration at start.
      result.push(
        new ConcTime({
          interval: 'administered',
          time: new Date(start),
          concentration: startConc,
          concentrationUnit: 'mcg/mL',
          calculation: calculationType,
        }),
      );

      let hrToCalculate = Math.ceil(infusionsStart);
      const endHour = infusionsStart + dose.infusionHrs;
      if (hrToCalculate - infusionsStart < 0.01) {
        //pretty much overlaps
        hrToCalculate++;
      }
      while (hrToCalculate < endHour) {
        const infusionTime = hrToCalculate - infusionsStart;
        const time = moment(start).add(infusionTime, 'hours').toDate();
        const concentration = concCalculator.concentrationDuringInfusion(
          startConc,
          dosePerHr,
          infusionTime,
          hrToCalculate,
        );
        result.push(
          new ConcTime({
            interval: 'administered',
            time: new Date(time),
            concentration: concentration,
            concentrationUnit: 'mcg/mL',
            calculation: calculationType,
          }),
        );
        hrToCalculate += 1;
      }

      // add infusion end
      result.push(
        new ConcTime({
          interval: 'administered',
          time: new Date(dose.infusionEndDt),
          concentration: dose.infusionEndConcentration,
          concentrationUnit: 'mcg/mL',
          calculation: calculationType,
        }),
      );

      if (doseNum < adminDoses.length - 1) {
        const endHour = adminDoses[doseNum + 1]!.startHr;
        const infusionEnd = dose.startHr + dose.infusionHrs;
        const start = dose.infusionEndDt;
        const startConcentration = dose.infusionEndConcentration;
        let hrToCalculate = Math.ceil(infusionEnd);
        if (hrToCalculate - infusionEnd < 0.01) {
          //pretty much overlaps
          hrToCalculate++;
        }
        while (hrToCalculate < endHour) {
          const decayTime = hrToCalculate - infusionEnd;
          const time = moment(start).add(decayTime, 'hours').toDate();
          const concentration = concCalculator.concentrationDuringDecay(
            startConcentration,
            decayTime,
            hrToCalculate,
          );
          result.push(
            new ConcTime({
              interval: 'administered',
              time: new Date(time),
              concentration: concentration,
              concentrationUnit: 'mcg/mL',
              calculation: calculationType,
            }),
          );
          hrToCalculate += 1;
        }
      }
      doseNum++;
    }
    // Ensure strictly ascending time order (floating-point rounding in Math.ceil
    // can occasionally produce out-of-order points when startHr lands on a whole number)
    result.sort((a, b) => a.time.getTime() - b.time.getTime());
    return result;
  }

  private generateAdministeredDoseConcentrations() {
    if (this._model) {
      console.log('generateAdministeredDoseConcentrations');

      const doseGenerator = () => this.adminDoseGenerator();
      const popCalculator = this.getCalculator(CalculationType.POPULATION, doseGenerator);
      const patCalculator = this.getCalculator(CalculationType.PATIENT, doseGenerator);
      if (popCalculator != null) {
        this._administeredDoses[CalculationType.POPULATION] =
          this.calculateAdministered(popCalculator);
        this._administeredDoseConc[CalculationType.POPULATION] = this.calculateAdminConcentrations(
          CalculationType.POPULATION,
          this._administeredDoses[CalculationType.POPULATION],
          popCalculator,
        );
      }
      if (patCalculator != null) {
        this._administeredDoses[CalculationType.PATIENT] =
          this.calculateAdministered(patCalculator);
        this._administeredDoseConc[CalculationType.PATIENT] = this.calculateAdminConcentrations(
          CalculationType.PATIENT,
          this._administeredDoses[CalculationType.PATIENT],
          patCalculator,
        );
      }
    }
  }

  private estimateLoadingDose(
    _doseAmount: number,
    _interval: number,
    _infusionDuration: number,
  ): number | null {
    if (this.hasAdministeredDoses()) {
      return null;
    }
    // estimate dose from model mg per kgdoseAmount
    const weight = this._patient?.weightKg() ?? null;
    if (weight !== null && this._model != null && this._model.default != null) {
      let estimatedDose: number | null = this._model?.default?.loadingDoseMgPerKg
        ? this._model.default.loadingDoseMgPerKg * weight
        : this._model?.default?.maintenanceDoseMgPerKg
          ? this._model.default.maintenanceDoseMgPerKg * weight
          : null;
      if (estimatedDose && this._drug?.dosingUnit) {
        // make in dosing unit amounts
        const numDoses = Math.ceil(estimatedDose / this._drug.dosingUnit);
        estimatedDose = numDoses * this._drug.dosingUnit;
      }
      return estimatedDose;
      if (estimatedDose) {
        return estimatedDose;
        // let estimateDosePerHour = estimatedDose / loadingDoseDuration;
        // if (this._drug?.maxDosePerHr && estimateDosePerHour > this._drug.maxDosePerHr) {
        //     estimatedDose = this._drug.maxDosePerHr * Math.floor(loadingDoseDuration);
        //     estimateDosePerHour = estimatedDose / loadingDoseDuration;
        // }
        // // console.log(`estimateLoadingDose doseAmout: ${doseAmount}, infusionDuration: ${infusionDuration}, loadingDoseDuration: ${loadingDoseDuration}, estimateDose: ${estimatedDose}, dosePerHour: ${dosePerHour}, estimateDosePerHour: ${estimateDosePerHour}`)
        // return dosePerHour < estimateDosePerHour ? estimatedDose : null
      }
    }
    return null;
  }

  private determineStartTime(interval: number): Date {
    if (this._delivery?.startDT) {
      const minStart = this.minimumSimulationStart();
      if (minStart != null) {
        if (this._delivery.startDT.getTime() >= minStart.getTime()) {
          return new Date(this._delivery.startDT);
        }
        console.error('Ignoring custom start before last administered dose');
      } else {
        return new Date(this._delivery.startDT);
      }
    } else if (this.hasAdministeredDoses()) {
      // start at interval time after last dose
      const doses = this._dosesByType[DoseTypes.DOSE] ?? [];
      const lastDose = doses[doses.length - 1]!;
      const lastDoseDT = lastDose.datetime;
      // can't have an interval smaller than infusion time.
      const nextDoseTime = addDate(
        lastDoseDT,
        Math.max(interval, lastDose.getInfusionDurationInHours()),
        'hours',
      );
      // Never propose a new dose before the last measured concentration —
      // doing so would place the proposal in a window that already has observed data.
      const concDoses = this._dosesByType[DoseTypes.CONC] ?? [];
      if (concDoses.length > 0) {
        const lastConcDT = concDoses[concDoses.length - 1]!.datetime;
        if (lastConcDT > nextDoseTime) {
          return new Date(lastConcDT);
        }
      }
      return nextDoseTime;
    }
    return new Date(); // now
  }

  private estimateIVSummaryResult(
    calculator: ConcentrationCalculator,
    calculationType: CalculationType,
    doseAmount: number,
    interval: number,
    infusionDuration: number,
    startTimeDelta = 0,
  ): SummaryResult {
    const mic = this._pathology?.mic ?? 1;
    if (calculator) {
      const estimatePeak = calculator.calculatePeak(
        interval,
        infusionDuration,
        doseAmount / infusionDuration,
        startTimeDelta,
      );
      const estimateTrough = calculator.calculateTrough(
        interval,
        infusionDuration,
        doseAmount / infusionDuration,
        startTimeDelta,
      );
      const estimateAuc = calculator.calculateAucFromPeakAndTrough(
        estimatePeak,
        estimateTrough,
        interval,
        infusionDuration,
        mic,
      );
      const estimateSS = doseAmount
        ? calculator.calculateSteadyState(doseAmount, interval, infusionDuration)
        : null;
      const tMic = calculator.calculatTmicFromPeak(estimatePeak, interval, infusionDuration, mic);
      const estimateTmic_percent = tMic && tMic < interval ? (tMic / interval) * 100 : 100;

      return new SummaryResult(
        calculationType,
        estimateAuc,
        estimatePeak,
        estimateTrough,
        estimateSS,
        tMic,
        estimateTmic_percent,
      );
    }
    console.error('input data not available to calculate summary result');
    return new SummaryResult(calculationType, 0, 0, 0, null, null, null);
  }

  private estimateContinousSummaryResult(
    calculator: ConcentrationCalculator,
    calculationType: CalculationType,
    doseAmount: number,
  ): SummaryResult {
    const mic = this._pathology?.mic ?? 1;
    if (calculator) {
      const estimatePeak = calculator.calculatePeak(24, 24, doseAmount);
      const estimateTrough = calculator.calculateTrough(24, 24, doseAmount);
      const estimateAuc = calculator.calculateAucFromPeakAndTrough(
        estimatePeak,
        estimateTrough,
        24,
        24,
        mic,
      );
      const estimateSS = doseAmount
        ? calculator.calculateSteadyState(doseAmount * 24, 24, 24)
        : null;
      const tMic = calculator.calculatTmicFromPeak(estimatePeak, 24, 24, mic);
      const estimateTmic_percent = tMic && tMic < 24 ? (tMic / 24) * 100 : 100;

      return new SummaryResult(
        calculationType,
        estimateAuc,
        estimatePeak,
        estimateTrough,
        estimateSS,
        tMic,
        estimateTmic_percent,
      );
    }
    console.error('input data not available to calculate summary result');
    return new SummaryResult(calculationType, 0, 0, 0, null, null, null);
  }

  private generateIVPlans() {
    if (this._drug == null) {
      return;
    }
    let method = null;
    for (const m of this._drug?.methods) {
      if (m.type == DeliveryMethod.IV) {
        method = m;
        break;
      }
    }
    if (method == null) {
      return; // nothing to generate
    }

    const intervals: number[] | null | undefined = method?.intervalRange;
    const doseAmounts: number[] | null | undefined = method?.amountRange;
    const infusionDurations: number[] | null | undefined = method?.infusionRange;
    const maxDosePerHr: number | null | undefined = this._drug?.maxDosePerHr;

    // Build the list of (dose, infusion, interval) triples to simulate
    let doseCombinations: { amount: number; infusionTime: number; interval: number }[];

    if (method.standardDoses?.length) {
      // Use explicitly-defined standard combinations (includes interval per triple)
      doseCombinations = method.standardDoses;
    } else if (intervals != null && doseAmounts != null && infusionDurations != null) {
      // Generate cross-product fallback
      doseCombinations = [];
      for (const interval of intervals) {
        for (const doseAmount of doseAmounts) {
          for (const infusionDuration of infusionDurations) {
            if (maxDosePerHr && doseAmount / infusionDuration > maxDosePerHr) {
              continue; // dose is too high
            }
            doseCombinations.push({ amount: doseAmount, infusionTime: infusionDuration, interval });
          }
        }
      }
    } else {
      return; // nothing to generate
    }

    console.log('Generate IV places with doseCombinations', doseCombinations);

    const generatedPlans: DynamicDosePlan[] = [];
    for (const doseToSimulate of doseCombinations) {
      const doseAmount = doseToSimulate.amount;
      const infusionDuration = doseToSimulate.infusionTime;
      const interval = doseToSimulate.interval;

      // generate dose plan
      const loadingDose = this.estimateLoadingDose(doseAmount, interval, infusionDuration);
      const loadingDoseInfusion = Math.max(
        infusionDuration,
        loadingDose ? (this._drug?.maxDosePerHr ? loadingDose / this._drug?.maxDosePerHr : 6) : 6,
      );
      const loadingDoseInfo = loadingDose
        ? { dose: loadingDose, infusion: loadingDoseInfusion, interval: interval }
        : null;
      const startTime = this.determineStartTime(interval);
      const dosingGenerator = () =>
        this.doseGenerator(startTime, doseAmount, infusionDuration, interval, loadingDoseInfo);
      const popCalculator = this.getCalculator(CalculationType.POPULATION, dosingGenerator);
      const patCalculator = this.getCalculator(CalculationType.PATIENT, dosingGenerator);
      const startTimeDelta = this.hasAdministeredDoses()
        ? (startTime.getTime() - this._dosesByType[DoseTypes.DOSE]![0]!.datetime.getTime()) /
          (1000 * 60 * 60)
        : 0;
      let mainResult: SummaryResult | null = null;
      let secondaryResult: SummaryResult | null = null;
      if (patCalculator) {
        mainResult = this.estimateIVSummaryResult(
          patCalculator,
          CalculationType.PATIENT,
          doseAmount,
          interval,
          infusionDuration,
          startTimeDelta,
        );
        if (popCalculator) {
          secondaryResult = this.estimateIVSummaryResult(
            popCalculator,
            CalculationType.POPULATION,
            doseAmount,
            interval,
            infusionDuration,
            startTimeDelta,
          );
        }
      } else if (popCalculator) {
        mainResult = this.estimateIVSummaryResult(
          popCalculator,
          CalculationType.POPULATION,
          doseAmount,
          interval,
          infusionDuration,
          startTimeDelta,
        );
      } else {
        return; // failed to calculate anything
      }

      const doseRegime: DynamicDoseRegime = new DynamicDoseRegime(
        DeliveryMethod.IV,
        doseAmount,
        loadingDose,
        infusionDuration,
        interval,
        startTime,
        mainResult?.auc,
        mainResult?.peak,
        mainResult?.trough,
        mainResult?.ss,
      );

      const plan = new DynamicDosePlan(doseRegime, mainResult, secondaryResult);
      if (this._target) {
        plan.calculateHighlights(this._target);
      }
      generatedPlans.push(plan);
    }
    this._plansByDeliveryMethod[DeliveryMethod.IV] = generatedPlans;
  }

  private generateContinuousPlans() {
    if (this._drug == null) {
      return;
    }
    let method = null;
    for (const m of this._drug?.methods) {
      if (m.type == DeliveryMethod.CONTINUOUS) {
        method = m;
        break;
      }
    }
    if (method == null) {
      return; // nothing to generate
    }
    const doseAmounts: number[] | null | undefined = method?.amountRange;
    if (doseAmounts == null) {
      return; // nothing to generate
    }
    const generatedPlans: DynamicDosePlan[] = [];
    for (const doseAmount of doseAmounts) {
      // generate dose plan
      const dosePerHour = doseAmount / 24.0;
      const loadingDose = this.estimateLoadingDose(dosePerHour, 24, 24);
      const loadingDoseDuration = loadingDose
        ? this._drug?.maxDosePerHr
          ? loadingDose / this._drug?.maxDosePerHr
          : 6
        : null;
      const loadingDoseInfo = loadingDose
        ? {
            dose: <number>loadingDose,
            infusion: <number>loadingDoseDuration,
            interval: <number>loadingDoseDuration,
          }
        : null;
      const startTime = this.determineStartTime(0);
      let mainResult: SummaryResult | null = null;
      let secondaryResult: SummaryResult | null = null;

      const dosesGenerator = () =>
        this.doseGenerator(startTime, doseAmount, 24, 24, loadingDoseInfo);
      const popCalculator = this.getCalculator(CalculationType.POPULATION, dosesGenerator);
      const patCalculator = this.getCalculator(CalculationType.PATIENT, dosesGenerator);
      if (patCalculator) {
        mainResult = this.estimateContinousSummaryResult(
          patCalculator,
          CalculationType.PATIENT,
          dosePerHour,
        );
        if (popCalculator) {
          secondaryResult = this.estimateContinousSummaryResult(
            popCalculator,
            CalculationType.POPULATION,
            dosePerHour,
          );
        }
      } else if (popCalculator) {
        mainResult = this.estimateContinousSummaryResult(
          popCalculator,
          CalculationType.POPULATION,
          dosePerHour,
        );
      } else {
        return; // failed to calculate anything
      }

      const doseRegime: DynamicDoseRegime = new DynamicDoseRegime(
        DeliveryMethod.CONTINUOUS,
        doseAmount,
        loadingDose,
        24,
        24,
        startTime,
        mainResult.auc,
        mainResult?.peak,
        mainResult?.trough,
        mainResult?.ss,
      );

      const plan = new DynamicDosePlan(doseRegime, mainResult, secondaryResult);
      if (this._target) {
        plan.calculateHighlights(this._target);
      }
      generatedPlans.push(plan);
    }
    this._plansByDeliveryMethod[DeliveryMethod.CONTINUOUS] = generatedPlans;
  }

  private generateDosePlans() {
    this.generateIVPlans();
    this.generateContinuousPlans();
  }

  addInfusionConcentrations(
    calculator: ConcentrationCalculator,
    start: Date,
    end: Date,
    dosePerHr: number,
    startConc: number,
    includeFirst: boolean,
    template: { interval: string; concentrationUnit: string; calculation?: string | null },
    existing: ConcTime[] | null | undefined,
    timeOrigin?: Date,
  ): ConcTime[] {
    const result: ConcTime[] = existing ?? [];

    // timeOrigin overrides the series anchor for timeDelta — needed when the 2-compartment
    // calculator measures doses from a fixed origin (first administered dose) that is earlier
    // than the start of this result array.
    const actualStart = timeOrigin ?? (existing && existing.length > 0 ? existing[0]!.time : start);

    if (includeFirst) {
      result.push(
        new ConcTime({
          interval: template.interval,
          time: new Date(start),
          concentration: startConc,
          concentrationUnit: template.concentrationUnit,
          calculation: template.calculation,
        }),
      );
    }

    const hrsToDose = (end.getTime() - start.getTime()) / (1000.0 * 60 * 60);
    let hrsSinceStart = 1;

    while (hrsSinceStart < hrsToDose) {
      const time = moment(start).add(hrsSinceStart, 'hours').toDate();
      const timeDelta = (time.getTime() - actualStart.getTime()) / (1000.0 * 60 * 60);
      const concentration = calculator.concentrationDuringInfusion(
        startConc,
        dosePerHr,
        hrsSinceStart,
        timeDelta,
      );
      result.push(
        new ConcTime({
          interval: template.interval,
          time: new Date(time),
          concentration: concentration,
          concentrationUnit: template.concentrationUnit,
          calculation: template.calculation,
        }),
      );
      hrsSinceStart += 1;
    }
    // add final entry
    const timeDelta = (end.getTime() - actualStart.getTime()) / (1000.0 * 60 * 60);
    const finalConcentration = calculator.concentrationDuringInfusion(
      startConc,
      dosePerHr,
      hrsToDose,
      timeDelta,
    );
    result.push(
      new ConcTime({
        interval: template.interval,
        time: new Date(end),
        concentration: finalConcentration,
        concentrationUnit: template.concentrationUnit,
        calculation: template.calculation,
      }),
    );
    return result;
  }

  addDecayConcentrations(
    calculator: ConcentrationCalculator,
    start: Date,
    end: Date,
    startConc: number,
    template: { interval: string; concentrationUnit: string; calculation?: string | null },
    existing: ConcTime[] | null | undefined,
    timeOrigin?: Date,
  ): ConcTime[] {
    const result: ConcTime[] = existing ?? [];
    const actualStart = timeOrigin ?? (existing && existing.length > 0 ? existing[0]!.time : start);
    const hrsToDecay = (end.getTime() - start.getTime()) / (1000.0 * 60 * 60);
    let hrsSinceStart = 1;

    let lastConcentration = startConc;
    while (hrsSinceStart < hrsToDecay) {
      const time = moment(start).add(hrsSinceStart, 'hours').toDate();
      const timeDelta = (time.getTime() - actualStart.getTime()) / (1000.0 * 60 * 60);
      const concentration = calculator.concentrationDuringDecay(lastConcentration, 1, timeDelta);
      result.push(
        new ConcTime({
          interval: template.interval,
          time: new Date(time),
          concentration: concentration,
          concentrationUnit: template.concentrationUnit,
          calculation: template.calculation,
        }),
      );
      hrsSinceStart += 1;
      lastConcentration = concentration;
    }
    // add final entry
    const timeDelta = (end.getTime() - actualStart.getTime()) / (1000.0 * 60 * 60);
    const finalConcentration = calculator.concentrationDuringDecay(
      startConc,
      hrsToDecay,
      timeDelta,
    );
    result.push(
      new ConcTime({
        interval: template.interval,
        time: new Date(end),
        concentration: finalConcentration,
        concentrationUnit: template.concentrationUnit,
        calculation: template.calculation,
      }),
    );
    return result;
  }

  private calculateIVTimeSeries(
    calculator: ConcentrationCalculator,
    doseRegime: DynamicDoseRegime,
    calculationType: CalculationType,
    initialConcentration: number,
  ): ConcTime[] {
    if (calculator === null) {
      // data not available
      return [];
    }
    const result: ConcTime[] = [];
    let simulatatedHrs = 0;
    const hoursToSimulate = Math.max(72, 7 * doseRegime.interval);
    const template = {
      interval: 'IV',
      concentrationUnit: 'mcg/mL',
      calculation: calculationType,
    };

    // For 2-compartment models the calculator's concentrationAtTime() measures time from
    // the first administered dose (D1). All timeDelta values passed to addInfusionConcentrations
    // and addDecayConcentrations must share the same origin so superposition is correct.
    const timeOrigin: Date | undefined = this.hasAdministeredDoses()
      ? this._dosesByType[DoseTypes.DOSE]![0]!.datetime
      : undefined;

    if (!this.hasAdministeredDoses() && doseRegime.loadingDose) {
      // has loading dose. add it.
      const loadingDoseDuration = this._drug?.maxDosePerHr
        ? doseRegime.loadingDose / this._drug?.maxDosePerHr
        : doseRegime.infusionDuration;
      const loadingDosePerHour = this._drug?.maxDosePerHr
        ? this._drug?.maxDosePerHr
        : doseRegime.loadingDose / doseRegime.infusionDuration;
      const doseEndDate = moment(doseRegime.startTime).add(loadingDoseDuration, 'hours').toDate();
      const decayEndDate = moment(doseRegime.startTime)
        .add(Math.max(doseRegime.interval, loadingDoseDuration), 'hours')
        .toDate();
      this.addInfusionConcentrations(
        calculator,
        doseRegime.startTime,
        doseEndDate,
        loadingDosePerHour,
        initialConcentration,
        true,
        template,
        result,
        timeOrigin,
      );
      this.addDecayConcentrations(
        calculator,
        doseEndDate,
        decayEndDate,
        result[result.length - 1]!.concentration,
        template,
        result,
        timeOrigin,
      );
      simulatatedHrs += Math.max(doseRegime.interval, loadingDoseDuration);
    }
    const dosePerHr = doseRegime.dose / doseRegime.infusionDuration;
    console.log(
      `Calculate time series - interval = ${doseRegime.interval}, infusionDuration = ${doseRegime.infusionDuration}, dosePerHour = ${dosePerHr}`,
    );
    while (simulatatedHrs < hoursToSimulate) {
      const isFirst = result.length === 0;
      const lastEntry = isFirst
        ? { time: doseRegime.startTime, concentration: initialConcentration }
        : result[result.length - 1]!;
      const doseStartDate = lastEntry.time;
      const startConc = lastEntry.concentration;
      const doseEndDate = moment(doseStartDate).add(doseRegime.infusionDuration, 'hours').toDate();
      const decayEndDate = moment(doseStartDate).add(doseRegime.interval, 'hours').toDate();
      this.addInfusionConcentrations(
        calculator,
        doseStartDate,
        doseEndDate,
        dosePerHr,
        startConc,
        result.length === 0,
        template,
        result,
        timeOrigin,
      );
      this.addDecayConcentrations(
        calculator,
        doseEndDate,
        decayEndDate,
        result[result.length - 1]!.concentration,
        template,
        result,
        timeOrigin,
      );
      simulatatedHrs += doseRegime.interval;
    }
    // Ensure ascending time order before passing to graph
    result.sort((a, b) => a.time.getTime() - b.time.getTime());
    return result;
  }

  /*
   * Called from DynamicDosePlan to generate the predicted graph.
   */
  calculateTimeSeries(doseRegime: DynamicDoseRegime, calculationType: CalculationType): ConcTime[] {
    let loadingDoseInfo: { dose: number; infusion: number; interval: number } | null = null;
    if (doseRegime.deliveryMethod === DeliveryMethod.CONTINUOUS) {
      const loadingDoseDuration = doseRegime.loadingDose
        ? this._drug?.maxDosePerHr
          ? doseRegime.loadingDose / this._drug?.maxDosePerHr
          : 6
        : null;
      loadingDoseInfo = doseRegime.loadingDose
        ? {
            dose: <number>doseRegime.loadingDose,
            infusion: <number>loadingDoseDuration,
            interval: <number>loadingDoseDuration,
          }
        : null;
    } else {
      const loadingDoseInfusion = doseRegime.loadingDose
        ? this._drug?.maxDosePerHr
          ? doseRegime.loadingDose / this._drug?.maxDosePerHr
          : 6
        : 6;
      const loadingDoseInterval = Math.max(doseRegime.interval, loadingDoseInfusion);

      loadingDoseInfo = doseRegime.loadingDose
        ? {
            dose: <number>doseRegime.loadingDose,
            infusion: loadingDoseInfusion,
            interval: loadingDoseInterval,
          }
        : null;
    }

    const dosesGenerator = () =>
      this.doseGenerator(
        doseRegime.startTime,
        doseRegime.dose,
        doseRegime.infusionDuration,
        doseRegime.interval,
        loadingDoseInfo,
      );

    const calculator = this.getCalculator(calculationType, dosesGenerator);

    if (calculator === null) {
      // data not available
      return [];
    }
    let initialConcentration: number = 0.0;
    const startTimeDt = doseRegime.startTime.getTime();
    if (this.hasAdministeredDoses()) {
      const adminDosesConcentrations = this._administeredDoses[calculationType]!;
      const lastDoseConc = adminDosesConcentrations[adminDosesConcentrations.length - 1]!;
      const endOfDoseConc = lastDoseConc.infusionEndConcentration;
      const endOfDoseDt = lastDoseConc.infusionEndDt.getTime();
      const firstDoseDt = this._dosesByType[DoseTypes.DOSE]![0]!.datetime.getTime();
      const decayHrs = (startTimeDt - endOfDoseDt) / (1000 * 60 * 60);
      // 2-compartment: concentrationDuringDecay uses timeDelta from first dose, not from end-of-infusion
      const simStartTimeDelta = (startTimeDt - firstDoseDt) / (1000.0 * 60 * 60);
      initialConcentration = calculator.concentrationDuringDecay(
        endOfDoseConc,
        decayHrs,
        simStartTimeDelta,
      );
    }
    if (doseRegime.deliveryMethod === DeliveryMethod.CONTINUOUS) {
      return this.calculateContinuousTimeSeries(
        calculator,
        doseRegime,
        calculationType,
        initialConcentration,
      );
    } else if (doseRegime.deliveryMethod === DeliveryMethod.ORAL) {
      return this.calculateOralTimeSeries(
        calculator,
        doseRegime,
        calculationType,
        initialConcentration,
      );
    }
    return this.calculateIVTimeSeries(
      calculator,
      doseRegime,
      calculationType,
      initialConcentration,
    );
  }

  getAdministeredTimeSeries(calculationType: CalculationType, startTime: Date): ConcTime[] | null {
    if (!this.hasAdministeredDoses()) {
      return null;
    }
    const dosesGenerator = () => this.adminDoseGenerator();
    const calculator = this.getCalculator(calculationType, dosesGenerator);

    const givenDoses: ConcTime[] = this._administeredDoseConc[calculationType] ?? [];
    if (givenDoses.length == 0 || calculator === null) {
      return null;
    }

    const lastDose: ConcTime = givenDoses[givenDoses.length - 1]!;
    const lastDoseTime: Date = lastDose.time;
    const lastDoseConc: number = lastDose.concentration;
    const template = {
      interval: 'administered',
      concentrationUnit: 'mcg/mL',
      calculation: calculationType,
    };
    // decay the administered dose until the startTime
    const result = [...givenDoses];
    this.addDecayConcentrations(
      calculator,
      lastDoseTime,
      startTime,
      lastDoseConc,
      template,
      result,
    );
    return result;
  }

  private calculateContinuousTimeSeries(
    calculator: ConcentrationCalculator,
    doseRegime: DynamicDoseRegime,
    calculationType: CalculationType,
    initialConcentration: number,
  ): ConcTime[] {
    if (calculator === null) {
      // data not available
      return [];
    }
    const actualStart = this.hasAdministeredDoses()
      ? this._dosesByType[DoseTypes.DOSE]![0]!.datetime
      : doseRegime.startTime;
    const result: ConcTime[] = [];
    const dosePerHr = doseRegime.dose / doseRegime.infusionDuration;
    const label = `${dosePerHr}mg/hr`;
    // push first
    result.push(
      new ConcTime({
        interval: label,
        time: new Date(doseRegime.startTime),
        concentration: initialConcentration,
        concentrationUnit: 'mcg/mL',
        calculation: calculationType,
      }),
    );

    let simulatedHrs = 1;
    const hoursToSimulate = Math.max(72, 7 * doseRegime.interval);
    let concentration = initialConcentration;

    if (doseRegime.loadingDose) {
      // apply loading dose over first 6 hours.
      const loadingDoseDuration = this._drug?.maxDosePerHr
        ? doseRegime.loadingDose / this._drug?.maxDosePerHr
        : 6;
      const loadingDosePerHour = this._drug?.maxDosePerHr
        ? this._drug?.maxDosePerHr
        : doseRegime.loadingDose / 6.0;
      while (simulatedHrs <= loadingDoseDuration && simulatedHrs < hoursToSimulate) {
        const time = moment(doseRegime.startTime).add(simulatedHrs, 'hours').toDate();
        const timeDelta = (time.getTime() - actualStart.getTime()) / (1000.0 * 60 * 60);
        concentration = calculator.concentrationDuringInfusion(
          concentration,
          loadingDosePerHour,
          1,
          timeDelta,
        );
        result.push(
          new ConcTime({
            interval: label,
            time: time,
            concentration: concentration,
            concentrationUnit: 'mcg/mL',
            calculation: calculationType,
          }),
        );
        simulatedHrs += 1;
      }
    }

    while (simulatedHrs < hoursToSimulate) {
      const time = moment(doseRegime.startTime).add(simulatedHrs, 'hours').toDate();
      const timeDelta = (time.getTime() - actualStart.getTime()) / (1000.0 * 60 * 60);
      concentration = calculator.concentrationDuringInfusion(
        concentration,
        dosePerHr,
        1,
        timeDelta,
      );
      result.push(
        new ConcTime({
          interval: label,
          time: time,
          concentration: concentration,
          concentrationUnit: 'mcg/mL',
          calculation: calculationType,
        }),
      );
      simulatedHrs += 1;
    }
    return result;
  }

  private calculateOralTimeSeries(
    _calculator: ConcentrationCalculator,
    _doseRegime: DynamicDoseRegime,
    _calculationType: CalculationType,
    _initialConcentration: number,
  ): ConcTime[] {
    const result: ConcTime[] = [];
    return result;
  }

  public calculateCustom(custom: Custom, addToSimulation: boolean): DynamicDosePlan | null {
    let startTime: Date | null | undefined = custom.startDt;
    let mainResult: SummaryResult | null = null;
    let secondaryResult: SummaryResult | null = null;
    const interval = convertToHours(custom.interval, custom.intervalUnit);
    const infusion = convertToHours(custom.duration, custom.durationUnit);
    let loadingDose = null;
    let loadingDoseInfusion = null;
    let loadingDoseInterval = null;
    if (custom.method == DeliveryMethod.CONTINUOUS) {
      if (custom.loading) {
        loadingDose = this.estimateLoadingDose(custom.amount, 24, 24);
        loadingDoseInfusion = loadingDose
          ? this._drug?.maxDosePerHr
            ? loadingDose / this._drug?.maxDosePerHr
            : 6
          : 6;
        loadingDoseInterval = loadingDoseInfusion;
      }
      if (startTime == null) {
        startTime = this.determineStartTime(0);
      }
      const dosePerHour = custom.amount / 24.0;
      const loadingDoseInfo = custom.loading
        ? {
            dose: <number>loadingDose,
            infusion: <number>loadingDoseInfusion,
            interval: <number>loadingDoseInterval,
          }
        : null;
      const doseGenerator = () =>
        this.doseGenerator(<Date>startTime, custom.amount, 24, 24, loadingDoseInfo);
      const popCalculator = this.getCalculator(CalculationType.POPULATION, doseGenerator);
      const patCalculator = this.getCalculator(CalculationType.PATIENT, doseGenerator);
      if (patCalculator) {
        mainResult = this.estimateContinousSummaryResult(
          patCalculator,
          CalculationType.PATIENT,
          dosePerHour,
        );
        if (popCalculator) {
          secondaryResult = this.estimateContinousSummaryResult(
            popCalculator,
            CalculationType.POPULATION,
            dosePerHour,
          );
        }
      } else if (popCalculator) {
        mainResult = this.estimateContinousSummaryResult(
          popCalculator,
          CalculationType.POPULATION,
          dosePerHour,
        );
      } else {
        return null; // failed to calculate anything
      }
    } else if (custom.method == DeliveryMethod.IV) {
      if (custom.loading) {
        loadingDose = this.estimateLoadingDose(custom.amount, interval, infusion);
        loadingDoseInfusion = Math.max(
          infusion,
          loadingDose ? (this._drug?.maxDosePerHr ? loadingDose / this._drug?.maxDosePerHr : 6) : 6,
        );
        loadingDoseInterval = Math.max(interval, loadingDoseInfusion);
      }
      if (startTime == null) {
        startTime = this.determineStartTime(custom.interval);
      }
      const loadingDoseInfo = custom.loading
        ? {
            dose: <number>loadingDose,
            infusion: <number>loadingDoseInfusion,
            interval: <number>loadingDoseInterval,
          }
        : null;
      const doseGenerator = () =>
        this.doseGenerator(<Date>startTime, custom.amount, infusion, interval, loadingDoseInfo);
      const popCalculator = this.getCalculator(CalculationType.POPULATION, doseGenerator);
      const patCalculator = this.getCalculator(CalculationType.PATIENT, doseGenerator);
      const startTimeDelta = this.hasAdministeredDoses()
        ? ((<Date>startTime).getTime() -
            this._dosesByType[DoseTypes.DOSE]![0]!.datetime.getTime()) /
          (1000 * 60 * 60)
        : 0;
      if (patCalculator) {
        mainResult = this.estimateIVSummaryResult(
          patCalculator,
          CalculationType.PATIENT,
          custom.amount,
          interval,
          infusion,
          startTimeDelta,
        );
        if (popCalculator) {
          secondaryResult = this.estimateIVSummaryResult(
            popCalculator,
            CalculationType.POPULATION,
            custom.amount,
            interval,
            infusion,
            startTimeDelta,
          );
        }
      } else if (popCalculator) {
        mainResult = this.estimateIVSummaryResult(
          popCalculator,
          CalculationType.POPULATION,
          custom.amount,
          interval,
          infusion,
          startTimeDelta,
        );
      } else {
        return null; // failed to calculate anything
      }
    } else {
      console.error(
        `Error calculating custom dosing plan, don't know how to process method ${custom.method}`,
      );
      return null; // failed to calculate anything
    }

    const doseRegime: DynamicDoseRegime = new DynamicDoseRegime(
      custom.method,
      custom.amount,
      loadingDose,
      custom.method == DeliveryMethod.CONTINUOUS ? 24 : infusion,
      custom.method == DeliveryMethod.CONTINUOUS ? 24 : interval,
      startTime,
      mainResult.auc,
      mainResult?.peak,
      mainResult?.trough,
      mainResult?.ss,
    );

    const plan = new DynamicDosePlan(doseRegime, mainResult, secondaryResult);
    if (this._target) {
      plan.calculateHighlights(this._target);
    }
    plan.calculateTimeSeries(this);

    if (addToSimulation) {
      if (custom.method in this._plansByDeliveryMethod) {
        this._plansByDeliveryMethod[custom.method]!.push(plan);
      } else {
        this._plansByDeliveryMethod[custom.method] = [plan];
      }
    }
    return plan;
  }

  public minimumSimulationStart(): Date | null {
    if (this.hasAdministeredDoses()) {
      const dosesSorted = this._dosesByType[DoseTypes.DOSE];
      if (dosesSorted && dosesSorted.length > 0) {
        const lastAdminDose = dosesSorted[dosesSorted.length - 1]!;

        return addDate(lastAdminDose.datetime, lastAdminDose.getInfusionDurationInHours(), 'hours');
      }
    }
    return null;
  }

  *adminDoseGenerator() {
    if (this.hasAdministeredDoses()) {
      const dosesSorted = this._dosesByType[DoseTypes.DOSE] ?? [];
      const iterator = dosesSorted.values();

      let currentAdmin = iterator.next();
      let nextAdmin = iterator.next();
      let startHr = 0;
      while (!nextAdmin.done) {
        const interval =
          (nextAdmin.value!.datetime.getTime() - currentAdmin.value!.datetime.getTime()) /
          (1000.0 * 60 * 60);
        yield {
          startHr: startHr,
          dose: currentAdmin.value!.amount,
          infusion: currentAdmin.value!.getInfusionDurationInHours(),
          interval: interval,
        };
        currentAdmin = nextAdmin;
        startHr += interval;
        nextAdmin = iterator.next();
      }
      // yield last administered dose simulation
      const finalInfusion = currentAdmin.value!.getInfusionDurationInHours();
      yield {
        startHr: startHr,
        dose: currentAdmin.value!.amount,
        infusion: finalInfusion,
        interval: finalInfusion + 24, // last interval is not known
      };
    }
  }

  *doseGenerator(
    startTime: Date, // start time of simulation
    dose: number, // standard dose amount
    infusion: number, // infusion in hours
    interval: number, // interval in hour
    loadingDose: null | { dose: number; infusion: number; interval: number },
  ) {
    let simulationStartHr = 0;

    if (this.hasAdministeredDoses()) {
      const adminDosesConcentrations = this._administeredDoses[CalculationType.POPULATION]!;

      const iterator = adminDosesConcentrations.values();

      let currentAdmin = iterator.next();
      let nextAdmin = iterator.next();
      simulationStartHr =
        (startTime.getTime() - currentAdmin.value!.dose.datetime.getTime()) / (1000.0 * 60 * 60);
      while (!nextAdmin.done) {
        yield {
          startHr: currentAdmin.value!.startHr,
          dose: currentAdmin.value!.dose.amount,
          infusion: currentAdmin.value!.infusionHrs,
          interval: nextAdmin.value!.startHr - currentAdmin.value!.startHr,
        };
        currentAdmin = nextAdmin;
        nextAdmin = iterator.next();
      }
      // yield last administered dose simulation
      yield {
        startHr: currentAdmin.value!.startHr,
        dose: currentAdmin.value!.dose.amount,
        infusion: currentAdmin.value!.infusionHrs,
        interval: simulationStartHr - currentAdmin.value!.startHr,
      };
    } else if (loadingDose) {
      simulationStartHr = Math.max(loadingDose.interval, loadingDose.infusion);
      yield {
        startHr: 0,
        dose: loadingDose.dose,
        infusion: loadingDose.infusion,
        interval: Math.max(loadingDose.interval, loadingDose.infusion),
      };
      simulationStartHr = Math.max(loadingDose.interval, loadingDose.infusion);
    }

    let simulated = 0;

    while (true) {
      yield {
        startHr: simulationStartHr + simulated,
        dose: dose,
        infusion: infusion,
        interval: interval,
      };
      simulated += interval;
    }
  }
}
